import axios from 'axios';
import { Request } from './request';
import customToast, { ToastType } from '../components_/Toast';
import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { baseUrl_, cacApiKey, cacBaseUrl } from '../utils/constants';

const getRecoverPasswordLink = ({ email }: { email: string }) =>
  Request.get(`/auth/resetpassword?email=${email?.toLowerCase()}`)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      customToast(error?.response?.data?.message ?? 'Could not send request', ToastType.error);
      throw error?.response?.data;
    });

const getTbhSecurityQuestions = () =>
  Request.get(`/verify/securityquestion`, { headers: { Authorization: '', sessId: '' } })
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      customToast(
        error?.response?.data?.message ??
          `Could not get platform's security questions, please refresh`,
        ToastType.error,
      );
      throw error?.response?.data;
    });

const getUserSecurityQuestions = ({ email, token }: { email: string; token: string }) =>
  Request.get(`/verify/securityquestion/${email}`, {
    headers: {
      'email-token': token,
    },
  })
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      customToast(
        error?.response?.data?.message ?? `Could not get user's security questions`,
        ToastType.error,
      );
      throw error?.response?.data;
    });

// MUTATIONS
const verifySecurityQuestionAnswer = (data: { data: string }) => {
  return Request.post(`/verify/verifysecurityquestion`, data)
    .then((res) => res.data)
    .catch((error) => {
      console.log(error);
      throw error?.response?.data;
    });
};
const updatePassword = (data: { data: string }) => {
  return Request.patch(`/auth/password`, data)
    .then((res) => res.data)
    .catch((error) => {
      throw error?.response?.data;
    });
};
const getAuthOtp = (data: { data: string }) => {
  return Request.post(`/verify/send-login-otp`, data)
    .then((res) => res.data)
    .catch((error) => {
      throw error?.response?.data;
    });
};
const setUserSecurityQuestion = ({ data, token }: { data: { data: string }; token: string }) => {
  return Request.post(`/verify/securityquestion`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => res.data)
    .catch((error) => {
      throw error?.response?.data;
    });
};

const verifyOtp = (data: { data: string }) => {
  return Request.post(`/verify/login-otp`, data)
    .then((res) => res.data)
    .catch((error) => {
      throw error?.response?.data;
    });
};

const cacNameCheck = (data: { proposedName: string; lineOfBusiness: string }) => {
  return axios
    .post(`${cacBaseUrl}/engine/pre/bn-compliance`, data, {
      headers: {
        X_API_KEY: cacApiKey as string,
        'Content-type': 'application/json',
      },
    })
    .then((res) => res.data)
    .catch((err) => {
      throw err.response.data;
    });
};

export const useGetPasswordRecoveryLink = ({ email }: { email: string }) => {
  const { isSuccess, isError, isFetching, refetch, isLoading } = useQuery({
    queryKey: ['getPasswordRecoveryLink', email],
    queryFn: () => getRecoverPasswordLink({ email }),
    enabled: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    gcTime: 0,
  });

  return { isSuccess, isError, isFetching, refetch, isLoading };
};

export const useGetTbhSecurityQuestions = () => {
  return useQuery({
    queryKey: ['getTbhSecurityQuestions'],
    queryFn: () => getTbhSecurityQuestions(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useGetUserSecurityQuestions = ({ email, token }: { email: string; token: string }) => {
  return useQuery({
    queryKey: ['getUserSecurityQuestions'],
    queryFn: () => getUserSecurityQuestions({ email, token }),
    enabled: !!email && !!token,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useCacNameCheck = () => {
  const mutation = useMutation({
    mutationFn: (data: { proposedName: string; lineOfBusiness: string }) => cacNameCheck(data),
  });

  return mutation;
};

export const useVerifySecurityQuestionAnswer = () => {
  const mutation = useMutation({
    mutationFn: (data: { data: string }) => verifySecurityQuestionAnswer(data),
  });

  return mutation;
};

export const useUpdatePassword = () => {
  const mutation = useMutation({
    mutationFn: (data: { data: string }) => updatePassword(data),
  });

  return mutation;
};

export const useSetUserSecurityQuestions = () => {
  return useMutation({
    mutationFn: ({ data, token }: { data: { data: string }; token: string }) =>
      setUserSecurityQuestion({ data, token }),
  });
};

export const useGetAuthOtp = () => {
  const mutation = useMutation({
    mutationFn: (data: { data: string }) => getAuthOtp(data),
  });

  return mutation;
};

export const useVerifyEmailOtp = () => {
  const mutation = useMutation({
    mutationFn: (data: { data: string }) => verifyOtp(data),
  });

  return mutation;
};
